/**
 * Admin Product Hub (Iter 9.29) - umbrella page for all product surfaces.
 * Routes:
 *   /admin/product                 -> this hub (cards)
 *   /admin/product/category-master -> AdminCategoryMasterPage
 *   /admin/product/applications    -> AdminApplicationsPage
 *   /admin/product/master-products -> AdminMasterProductsPage
 *   /admin/product/templates       -> /me Template Editor
 *   /admin/product/defaults        -> DefaultsInspoAdminPage
 *   /admin/product/service-catalog -> CategoryManagementPage
 */
import { useNavigate } from "react-router-dom";
import {
  Layers, Mail, Package, Layout, Sparkles, ListChecks, ArrowLeft,
  Tag, Star, Info, Megaphone, BookOpen, MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminFieldInfo from "@/components/admin/AdminFieldInfo";

const TILES = [
  {
    path: "/admin/product/category-master",
    label: "Category Settings",
    icon: Layers,
    blurb: "ONE source for category logo, light/dark hex, label, copy. Admin truth.",
    info: {
      description: "Canonical 12-category control. Edits sync to host dashboards, sales pages, envelopes, intake question sets.",
      syncs_to: ["Host dashboards", "Sales / demo pages", "Envelope animation", "Plan-a-service modal"],
      source: "Mongo: categories (merged from category_masters in iter 9.33)",
    },
  },
  {
    path: "/admin/product/applications",
    label: "Pipeline & Waitlist",
    icon: Mail,
    blurb: "Unified feed of waitlist + intake submissions with full status workflow.",
    info: {
      description: "One feed: waitlist → dashboard_pending → activated. Same data shows up in People → Host Pipeline kanban.",
      syncs_to: ["People → Host Pipeline", "Beehiiv tags", "Host dashboard activation"],
      source: "Mongo: waitlist + host_applications",
    },
  },
  {
    path: "/admin/product/master-products",
    label: "Master Products",
    icon: Package,
    blurb: "Global product catalog with brand-logo + image. Used everywhere products show.",
    info: {
      description: "Catalog of products users can link to preference cards / services. Hosts can submit; admin promotes.",
      syncs_to: ["Plan-a-service Link picker", "Host product picker", "Preference card detail"],
      source: "Mongo: master_products + brands",
    },
  },
  {
    path: "/admin/product/brands",
    label: "Brands",
    icon: Tag,
    blurb: "Brand database with logos. Linked to products → logo shows in pickers + cards.",
    info: {
      description: "Brand catalog. Each brand has a Cloudinary logo. Master products link via brand_id, surfacing the logo platform-wide.",
      syncs_to: ["Master Products picker", "Search results", "Host product cards"],
      source: "Mongo: brands",
    },
  },
  {
    path: "/admin/product/service-catalog",
    label: "Service Catalog",
    icon: ListChecks,
    blurb: "Per-category service taxonomy. See which hosts link to each canonical service.",
    info: {
      description: "Master service taxonomy (e.g. 'Bridal Makeup', 'Silk Press'). Hosts link to these to roll up into platform insights.",
      syncs_to: ["Host services tab", "Plan-a-service category picker", "Insights buckets"],
      source: "Mongo: service_catalog",
    },
  },
  {
    path: "/admin/product/templates",
    label: "/me + Host Template Editor",
    icon: Layout,
    blurb: "Toggle, reorder, edit every /me + host dashboard section.",
    info: {
      description: "Controls layout, section order, copy and visibility for /me + host dashboards.",
      syncs_to: ["/me Dashboard for every user", "Host dashboard for every category"],
      source: "Mongo: company_settings + me_template + host_dashboard_templates",
    },
  },
  {
    path: "/admin/copy-editor",
    label: "Copy Editor",
    icon: MessageSquare,
    blurb: "Every UI string. Airtable grid, no AI rewrite, manual edit only.",
    info: {
      description: "152+ keys mapped to screens / modals / buttons. Edits sync instantly via useAdminCopy.",
      syncs_to: ["Every component using useAdminCopy hook"],
      source: "Mongo: admin_copy",
    },
  },
  {
    path: "/admin/legacy-config",
    label: "Portals",
    icon: Star,
    blurb: "Pilot, investor, advocate, team portals. Pilot portals grouped by category.",
    info: {
      description: "All portal templates in one place. Pilot portals appear nested under their category.",
      syncs_to: ["/private/{slug}", "/me access surfaces"],
      source: "Mongo: private_portals + portal_templates",
    },
  },
  {
    path: "/admin/legacy-config",
    label: "Help & FAQ",
    icon: Info,
    blurb: "FAQ items + linked CTAs. Targetable by user type, category, portal.",
    info: {
      description: "FAQ items shown in the floating Help widget. Each item can target user types and have a linked CTA URL.",
      syncs_to: ["Floating Help widget", "Portal Help section"],
      source: "Mongo: faq_items",
    },
  },
  {
    path: "/admin/updates",
    label: "Updates",
    icon: Megaphone,
    blurb: "Targetable updates: by user type (pilot, subscribed, advocate), category, portal.",
    info: {
      description: "Push announcements to specific user segments. Visible in Help widget + portal Updates section + /me messages.",
      syncs_to: ["Help widget", "Portal updates", "/me messages"],
      source: "Mongo: updates",
    },
  },
  {
    path: "/admin/messages",
    label: "Messages",
    icon: MessageSquare,
    blurb: "All inbound user help messages, organized by user type + category.",
    info: {
      description: "Inbox for messages users send via /me Inner Circle / portal contact form. Filterable.",
      syncs_to: ["/me Messages tab", "Portal messaging"],
      source: "Mongo: user_messages",
    },
  },
  {
    path: "/admin/defaults",
    label: "Defaults & Inspo",
    icon: Sparkles,
    blurb: "Curated inspo folders + preference schemas (per-category seeds).",
    info: {
      description: "Per-category default inspo galleries + preference schemas.",
      syncs_to: ["Inspo picker", "Preference card seed defaults"],
      source: "Mongo: inspo_folders",
    },
  },
  {
    path: "/admin/docs",
    label: "Docs",
    icon: BookOpen,
    blurb: "All product / process docs in one searchable index.",
    info: {
      description: "PRD, playbooks, screen maps, process docs.",
      syncs_to: ["Static markdown rendered in /admin/docs"],
      source: "/app/memory/*.md",
    },
  },
];

export default function AdminProductHubPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-product-hub">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-20">
        <div className="max-w-[1700px] mx-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => navigate("/admin")} data-testid="product-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Layers className="w-5 h-5 text-indigo-600" />
          <h1 className="text-lg font-semibold">Product</h1>
          <AdminFieldInfo
            description="Product umbrella - every catalog, branding, intake question, default preference, and template that drives the customer experience."
            syncs_to={["Host dashboards", "Consumer /me", "Sales pages", "Envelopes"]}
            source="Mongo: category_masters, master_products, master_service_types, waitlist, host_applications, inspo_folders, company_settings"
            testid="product-hub"
          />
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((t) => {
            const Icon = t.icon;
            // Iter 9.29: use the last segment of the full path so /admin/category-management
            // becomes "service-catalog" testid (matches review spec).
            const slug = (t.testid_slug || t.path.split("/").pop()).replace(/^category-management$/, "service-catalog");
            return (
              <button
                key={t.path}
                onClick={() => navigate(t.path)}
                className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-indigo-400 hover:shadow-sm transition-all group"
                data-testid={`product-tile-${slug}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-100 transition">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm">{t.label}</h3>
                      <AdminFieldInfo {...t.info} testid={`tile-${slug}`} />
                    </div>
                    <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">{t.blurb}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
